import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';

import type { HistoryStepType } from '@poseidon/components/HistoryStep';
import { cn } from '@poseidon/components/cn';
import { Tooltip } from '@poseidon/components/Input2';
import { Communication, Development, Editor, General, Time, Users } from '@poseidon/components/StrokeIcons';

import type { HistoryStepSummary } from '~/components/Sequence/sequenceHistory';
import { stepDateLabel } from '~/components/Sequence/sequenceHistory';
import { SequenceHistoryFaceStack } from '~/components/Sequence/SequenceHistoryFaceStack';
import { PREVIEW_GAP, PREVIEW_W } from '~/components/Sequence/sequenceLayout';
import { STEP_META, STEP_TILE_CLASS, STEP_TILE_COLORS } from '~/components/Sequence/sequenceSteps';

/** A row's height — the graph's own icon tile (36px) plus a little air, room for two lines of text beside it. */
export const HISTORY_STEP_ROW_H = 40;
/** The tile's half-width: the tile is centred ON the spine. */
const TILE_HALF = 18;
/** Tile → the face(s) on its left. */
const NEAR_GAP = 8;
/** The face slot: room for a conversation's two overlapping faces, so every row's date ends on one line. */
const FACES_W = 40;

/**
 * The tile's icon for every kind of entry — the graph's own step icon where the entry is the trace of a step
 * (a sent message wears the Message tile, an invite the Connect tile), its own icon otherwise. Every kind has
 * one, so no entry draws an empty tile; and each is a bare svg, so the tile sizes and colours it like the
 * graph's own (a tinted tile tints its icon too).
 */
const TILE_ICON: Record<HistoryStepType, ReactNode> = {
  message: STEP_META.message.icon,
  inmail: STEP_META.inmail.icon,
  declinedInmail: STEP_META.inmail.icon,
  mail: STEP_META.email.icon,
  mailReplied: STEP_META.email.icon,
  connect: STEP_META.connect.icon,
  connectAccept: STEP_META.connect.icon,
  call: STEP_META.call.icon,
  postsLike: STEP_META.like.icon,
  follow: STEP_META.follow.icon,
  reminder: STEP_META.reminder.icon,
  startedCadence: <Development.GitBranch01 />,
  cadenceStateChanged: <Development.GitBranch01 />,
  campaignDelete: <General.Trash01 />,
  document: <General.Eye />,
  visit: <General.Eye />,
  comment: <Editor.Pencil03 />,
  note: <Editor.Pencil03 />,
  stageChanged: <Development.Dataflow03 />,
  owner: <Users.User02 />,
  meetingBooked: <Time.Calendar />,
  interestDetected: <General.Zap />,
  contactUnavailable: <General.XCircle />,
  contactAvailable: <General.CheckCircle />,
  error: <Communication.AnnotationAlert />,
  unknown: <General.InfoCircle />,
};

interface Props {
  /** The step's id — what opening it reports, and what the popover anchors to. */
  entryId: string;
  summary: HistoryStepSummary;
  /** The spine's x and the column's width (content px). */
  spineX: number;
  width: number;
  open: boolean;
  onOpen: (entryId: string, anchor: Element) => void;
}

/**
 * One entry of the contact's history drawn the way the graph draws its own steps: an icon tile ON the spine,
 * what was said (or what happened) as faint text to its RIGHT, and to its LEFT the face of whoever did it and
 * when. A conversation's tile carries how many messages it folds; what YOU did gets a tinted tile. The
 * sentence of who did what ("You sent 3 messages") is the hover tooltip. Clicking it opens the full entry or
 * thread ({@link SequenceHistoryPopover}). `data-no-pan`, so pressing it opens it rather than starting a pan.
 */
export function SequenceHistoryStep({ entryId, summary, spineX, width, open, onOpen }: Props) {
  const openFrom = (el: Element) => onOpen(entryId, el);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    openFrom(e.currentTarget);
  };
  const textW = Math.max(120, Math.min(PREVIEW_W, width - (spineX + TILE_HALF + PREVIEW_GAP) - 16));
  return (
    <div className="relative" style={{ height: HISTORY_STEP_ROW_H }}>
      {/* The step starts at the tile; the date and face hang off its left edge (right-anchored, so a date of
          any width — "Dec 30, 2025 – Jan 2" — grows leftward and never pushes the face under the tile, and
          every row's date ends on the same line, like the graph's own sent dates). */}
      <Tooltip
        as="div"
        tooltip={open ? undefined : summary.title}
        data-no-pan
        data-history-step={entryId}
        data-from-you={summary.fromYou || undefined}
        role="button"
        tabIndex={0}
        aria-label={`${summary.title} — open`}
        onClick={(e: MouseEvent<HTMLDivElement>) => openFrom(e.currentTarget)}
        onKeyDown={onKeyDown}
        className="group absolute inset-y-0 flex cursor-pointer items-center rounded-lg"
        style={{ left: spineX - TILE_HALF }}>
        <div
          className="absolute right-full top-1/2 flex -translate-y-1/2 items-center gap-2"
          style={{ marginRight: NEAR_GAP }}>
          <div className="whitespace-nowrap text-[10px] font-medium text-lynch-400 dark:text-lynch-500">
            {stepDateLabel(summary.from, summary.at)}
          </div>
          <div aria-hidden className="flex shrink-0 justify-end" style={{ width: FACES_W }}>
            <SequenceHistoryFaceStack people={summary.people} />
          </div>
        </div>
        <div
          aria-hidden
          className={cn(
            'relative shrink-0 shadow-sm transition-shadow group-hover:shadow-md',
            STEP_TILE_CLASS,
            STEP_TILE_COLORS,
            summary.fromYou &&
              'border-blue-200 bg-blue-50 text-blue-600 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300',
            open && 'border-blue-300 ring-2 ring-blue-200 dark:border-blue-500/60 dark:ring-blue-500/30',
          )}>
          {TILE_ICON[summary.stepType]}
          {summary.count > 1 && (
            <span
              data-channel-badge
              className="absolute -bottom-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-lynch-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-slate-50 dark:bg-lynch-400 dark:ring-neutral-900">
              {summary.count}
            </span>
          )}
        </div>
        <div
          data-step-text
          className="line-clamp-2 text-sm leading-5 text-lynch-500 transition-colors group-hover:text-slate-700 dark:text-lynch-400 dark:group-hover:text-slate-200"
          style={{ marginLeft: PREVIEW_GAP, maxWidth: textW }}>
          {summary.snippet ?? summary.title}
        </div>
      </Tooltip>
    </div>
  );
}
